<script lang="ts" setup>
import type { CheckCallWithUpdate } from "@/api/trips/dto/check-call";
import CheckCallDateTime from "@/views/trips/view/components/CheckCallDateTime.vue";
import OmniTable from "@/components/OmniTable.vue";
import TripTimer from "@/components/trip/TripTimer.vue";
import type { TravelOrderWithTruck, TripRouteDto } from "@/api/trips/dto/trip.dto";
import { FilterUtils } from "@/filters";
import { useDateTime } from "@/composables";
import { useAppStore } from "@/store/app.store";
import { computed } from "vue";
import DismissedChip from "@/components/chips/DismissedChip.vue";
import { RouteStatus } from "@/data/trip";

const filters = new FilterUtils();
const { dateTZ } = useDateTime();
const { dispatcher } = useAppStore();

const tableHeaders = [
  { title: "Type", key: "type", sortable: false },
  { title: "Location", key: "locationAddress", class: "text-grey", sortable: false },
  { title: "Broker update", key: "brokerUpdate", sortable: false },
  { title: "Date time", key: "dateTime", sortable: false },
  { title: "Actions", key: "actions", sortable: false, align: "center", width: 178 },
];

const props = defineProps<{
  travelOrder: TravelOrderWithTruck;
  checkCalls: CheckCallWithUpdate[];
  route: TripRouteDto | undefined;
}>();

const emits = defineEmits<{
  (e: "viewUpdate", checkCall: CheckCallWithUpdate): void;
  (e: "updateMonitor"): void;
  (e: "openCreateUpdate", travelOrder: TravelOrderWithTruck, checkCall: CheckCallWithUpdate): void;
  (e: "openIntervalDialog", travelOrder: TravelOrderWithTruck): void;
}>();

const relatedUpdates = computed(() => {
  return props.checkCalls
    .filter((checkCall) => {
      const isRouteIdMatched = props.travelOrder.routeId === checkCall.routeId;

      if (!isRouteIdMatched) {
        return;
      }

      const travelOrderIds = checkCall.travelOrderIds;

      if (!travelOrderIds?.length) {
        return true;
      }

      return props.travelOrder.id && travelOrderIds.includes(props.travelOrder.id);
    })
    .reverse();
});

const isPlanned = computed(() => {
  return props.route?.status === RouteStatus.planned;
});

function getRowClass(row: Record<string, string> & { item: CheckCallWithUpdate }): string {
  if (row.item.deletedBy) {
    return "bg-grey-lighten-2";
  }

  return "";
}

function updatesTableProps(row: Record<string, string> & { item: CheckCallWithUpdate }): Record<string, string> {
  return {
    class: getRowClass(row),
  };
}

function getTime(createAt: string, checkCallTz: string = "") {
  return dateTZ(createAt || "", checkCallTz, "HH:mm");
}

function checkIsCreateUpdateDisabled(item: CheckCallWithUpdate) {
  return Boolean(item.deletedBy) || isPlanned.value;
}
</script>

<template>
  <div>
    <div class="d-flex align-center justify-space-between">
      <div class="d-flex align-center">
        <div class="font-size-16 font-weight-500 text-gray-darken-3 mr-4">Travel order #{{ travelOrder.id }}</div>

        <router-link
          :to="{ name: 'truck-view', params: { id: travelOrder.truck.id } }"
          class="text-decoration-none"
          target="_blank"
        >
          <v-icon class="mr-1" color="primary" size="14">mdi-truck-outline</v-icon>
          <span class="font-size-12 font-weight-500 text-primary">Truck: #{{ travelOrder.truck.number }}</span>
        </router-link>
      </div>

      <div class="d-flex align-center">
        <span class="text-grey-darken-1 mr-3">New update in:</span>
        <trip-timer
          :disabled="isPlanned"
          :interval="travelOrder.updateInterval"
          :next-update-time="travelOrder.nextUpdateTime"
          :truck-id="travelOrder.truck.id"
          :truck-number="travelOrder.truck.number"
          @open-dialog="emits('openIntervalDialog', travelOrder)"
        ></trip-timer>
      </div>
    </div>

    <omni-table
      :headers="tableHeaders"
      :items="relatedUpdates"
      :items-per-page="-1"
      :row-props="updatesTableProps"
      :server-data="false"
      class="mt-5"
    >
      <template #[`header.locationAddress`]="{ column }: { column?: { title?: string | undefined } }">
        <div class="py-2">
          {{ column?.title }},
          <div>Miles left to next stop</div>
        </div>
      </template>

      <template #[`item.type`]="{ item }: { item: CheckCallWithUpdate }">
        <div>
          <span> {{ item.type }}</span>
          <dismissed-chip v-if="item.deletedBy" class="ml-3" />
        </div>
      </template>

      <template #[`item.locationAddress`]="{ item }: { item: CheckCallWithUpdate }">
        {{ item.locationAddress }}
        <div class="font-size-12 text-grey-darken-1">
          Miles left to next stop: {{ filters.miles(item.nextStopDistance, 0) }}
        </div>
      </template>

      <template #[`item.dateTime`]="{ item }: { item: CheckCallWithUpdate }">
        <check-call-date-time :check-call="item" />
      </template>

      <template #[`item.brokerUpdate`]="{ item }: { item: CheckCallWithUpdate }">
        {{ item.update?.message }}
      </template>

      <template #[`item.actions`]="{ item }: { item: CheckCallWithUpdate }">
        <div v-if="item.update">
          <div class="d-flex align-center justify-space-between">
            <div class="font-weight-500 font-size-12 mr-2">
              Sent by <span class="text-primary">{{ item.update?.createdDispatcher.name }}</span>
              <span class="text-grey-darken-1">
                at {{ getTime(item.update?.createdAt, dispatcher?.timezone) }}
                {{ dateTZ(item.createdAt, dispatcher!.timezone, "z") }}</span
              >
            </div>

            <v-btn
              color="primary"
              density="compact"
              icon="mdi-eye-outline"
              variant="text"
              @click="emits('viewUpdate', item)"
            ></v-btn>
          </div>
        </div>
        <v-btn
          v-else
          :disabled="checkIsCreateUpdateDisabled(item)"
          class="text-uppercase"
          color="primary"
          variant="flat"
          @click="emits('openCreateUpdate', travelOrder, item)"
        >
          Create update
        </v-btn>
      </template>
      <template #bottom>
        <div class="d-flex justify-space-between bottom-row"></div>
      </template>
    </omni-table>
  </div>
</template>

<style lang="scss" scoped></style>
